import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import latinize from 'latinize';
import Highlighter, { HighlighterProps } from 'react-highlight-words';
import { alpha } from '@mui/material/styles';
import {
  Drawer,
  DrawerProps,
  Collapse,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Icon,
  ListItemText,
  Divider,
  List,
  IconButton,
  Paper,
  InputBase,
  Box,
  Tooltip,
  CircularProgress,
} from '@mui/material';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import styles from './styles';
import api from '../../services/api';

// Cast para evitar erro de incompatibilidade do React 18 com @types antigos
const HighlighterComponent = Highlighter as unknown as React.ComponentType<HighlighterProps>;

export type ItemMenuType = {
  id: number;
  parent: number;
  position: number;
  type: string;
  title: string;
  icon: string;
  path: string;
  situation: number;
  childrens?: ItemMenuType[]; // Nome original retornado pela sua API
  children?: ItemMenuType[]; // Nome alternativo padrão
};

interface MenuListProps extends DrawerProps {
  show: boolean;
  onClose: () => void;
}

interface MenuItemProps {
  item: ItemMenuType;
  textSearch: string;
  classes: Record<string, string>;
  onClose: () => void;
  level?: number;
}

function MenuItem({ item, textSearch, classes, onClose, level = 0 }: MenuItemProps) {
  const [open, setOpen] = useState(Boolean(textSearch));

  useEffect(() => {
    if (textSearch) {
      setOpen(true);
    }
  }, [textSearch]);

  if (item?.type === 'divider') {
    return <Divider key={`divider-${item.id}`} />;
  }

  // Suporta tanto 'childrens' (da sua API) quanto 'children'
  const childItems = item?.childrens || item?.children || [];

  const highlightTitle = (rawTitle: string) => {
    const titleText = rawTitle || '';
    return (
      <HighlighterComponent
        highlightClassName={classes?.resultTextHighlights}
        searchWords={textSearch ? textSearch.split(' ') : []}
        autoEscape
        textToHighlight={titleText}
        sanitize={str => (str ? latinize(str) : '')}
      />
    );
  };

  // Se for item normal OU pasta sem filhos
  if (item?.type === 'item' || childItems.length === 0) {
    return (
      <ListItem disablePadding sx={{ pl: level * 2 }}>
        <ListItemButton component={RouterLink} to={`/${item?.path || ''}`} onClick={onClose}>
          {item?.icon ? (
            <ListItemIcon>
              <Icon>{item.icon}</Icon>
            </ListItemIcon>
          ) : null}
          <ListItemText primary={highlightTitle(item?.title)} />
        </ListItemButton>
      </ListItem>
    );
  }

  // Se for pasta com filhos
  return (
    <Box sx={{ pl: level * 2 }}>
      <ListItemButton onClick={() => setOpen(prev => !prev)}>
        {item?.icon ? (
          <ListItemIcon>
            <Icon>{item.icon}</Icon>
          </ListItemIcon>
        ) : null}
        <ListItemText primary={highlightTitle(item?.title)} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding className={classes?.subMenuList}>
          {childItems.map(child => (
            <MenuItem
              key={`menu-item-${child.id}`}
              item={child}
              textSearch={textSearch}
              classes={classes}
              onClose={onClose}
              level={level + 1}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

export default function MenuListDrawer({ show = false, onClose }: MenuListProps) {
  const [data, setData] = useState<ItemMenuType[]>([]);
  const [initialData, setInitialData] = useState<ItemMenuType[]>([]); // Cache do menu completo
  const [textSearch, setTextSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const classes = styles();

  useEffect(() => {
    // Se o menu não estiver visível (Drawer fechado), não faz nada
    if (!show) return;

    let isMounted = true;

    // 1. Se a busca está vazia e JÁ temos o menu carregado em cache:
    if (!textSearch && initialData.length > 0) {
      setData(initialData); // Restaura o menu completo do cache instantaneamente
      return;
    }

    // 2. Busca na API apenas na 1ª abertura OU quando houver busca por texto
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get('intranet/configs/sidebar-menu', {
          params: { search: textSearch },
        });

        if (isMounted) {
          const menuData = Array.isArray(response?.data) ? response.data : response?.data?.data || [];

          setData(menuData);

          // Se foi a requisição inicial (sem filtro de busca), salva no cache
          if (!textSearch) {
            setInitialData(menuData);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar menu:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Aplica o delay de 400ms apenas enquanto digita
    const delay = textSearch ? 400 : 0;
    const timer = setTimeout(fetchData, delay);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [textSearch, show, initialData]);

  return (
    <Box className={classes?.root}>
      <Drawer anchor="left" open={show} onClose={onClose} className={classes?.container}>
        <Box className={classes?.searchRoot}>
          <Paper className={classes?.inputRoot}>
            <Box className={classes?.iconButton}>
              <SearchIcon />
            </Box>
            <InputBase
              className={classes?.input}
              placeholder="Pesquisar"
              inputProps={{ 'aria-label': 'pesquisar' }}
              value={textSearch}
              onChange={e => setTextSearch(e.target.value)}
            />
            <IconButton
              className={classes?.iconButton}
              onClick={() => setTextSearch('')}
              disabled={textSearch.length === 0}
              color="secondary"
              size="large"
            >
              <DeleteIcon />
            </IconButton>
          </Paper>

          <Tooltip title="Fechar menu">
            <IconButton className={classes?.closeIconButton} onClick={onClose} color="warning" size="large">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box className={classes?.menuList} role="presentation" sx={{ position: 'relative', minHeight: 200 }}>
          <List>
            {data?.map(item => (
              <MenuItem
                key={`root-${item.id}`}
                item={item}
                textSearch={textSearch}
                classes={classes}
                onClose={onClose}
              />
            ))}
          </List>

          {loading && (
            <Box
              sx={{
                // Adapta a cor de fundo com transparência baseada no tema ativo
                bgcolor: theme => alpha(theme.palette.background.paper, 0.7),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                inset: 0,
                zIndex: 10,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
